import { z } from "zod";
import { BasemapDraftDocument } from "./draft";
import {
  BASEMAP_COARSEST_LEVEL,
  BASEMAP_FINEST_LEVEL,
  BasemapFeatureSetRules,
  type BasemapManifest,
  BasemapName,
  BasemapRasterRules,
  Sha256,
} from "./manifest";
import { placeKey, type TilePlace } from "./tiles";
import { decodeDetailMap, LeafTree } from "./tree";

/**
 * A basemap draft's room: the rules every copy of a draft follows, so its Durable Object and each
 * editor apply the same commits the same way. A draft is an optional base, its document, each
 * raster's tree of leaves, the tiles someone changed, each named by its blob's hash, the whole
 * features of each feature set someone brought in, also a blob, and the overlays its authors
 * trace. A tile nobody changed is its raster's base's, or empty; a feature set nobody changed is
 * its base's.
 */

export const RoomPlace = z.strictObject({
  face: z.int().min(0).max(5),
  level: z.int().min(BASEMAP_COARSEST_LEVEL).max(BASEMAP_FINEST_LEVEL),
  x: z.int().min(0),
  y: z.int().min(0),
});

/**
 * Where a base's file is stored: `published`, a blob a published version lists, or `draft`, a file
 * the author uploaded into this draft, which publishing stores as a published file.
 */
export const RoomFileStorage = z.enum(["published", "draft"]);
export type RoomFileStorage = z.infer<typeof RoomFileStorage>;

/**
 * A raster's base: the tile file every place nobody changed is read from, its detail, and the
 * rules it was made with. Publishing copies the file's tiles byte for byte wherever nothing beneath
 * them changed and the rules are the same.
 */
export const RoomRasterBase = z
  .strictObject({
    tileFile: Sha256,
    finestLevel: z.int().min(BASEMAP_COARSEST_LEVEL).max(BASEMAP_FINEST_LEVEL),
    detailMap: z.string(),
    rules: BasemapRasterRules,
    storage: RoomFileStorage,
  })
  .meta({ id: "BasemapRoomRasterBase" });
export type RoomRasterBase = z.infer<typeof RoomRasterBase>;

/** A feature set's base: its clipped tile file, which publishing lists as it is. */
export const RoomFeatureSetBase = z
  .strictObject({
    tileFile: Sha256,
    detailMap: z.string(),
    rules: BasemapFeatureSetRules,
    storage: RoomFileStorage,
  })
  .meta({ id: "BasemapRoomFeatureSetBase" });
export type RoomFeatureSetBase = z.infer<typeof RoomFeatureSetBase>;

/** The package a draft began from, raster by raster and feature set by feature set. */
export const RoomBase = z
  .strictObject({
    rasters: z.record(BasemapName, RoomRasterBase),
    featureSets: z.record(BasemapName, RoomFeatureSetBase),
  })
  .meta({ id: "BasemapRoomBase" });
export type RoomBase = z.infer<typeof RoomBase>;

/**
 * A picture the authors trace, pinned over the world and drawn at an opacity. It stays in the room
 * and is never published.
 */
export const RoomOverlay = z
  .strictObject({
    id: z.uuid(),
    name: z.string().min(1).max(120),
    blob: Sha256.meta({ description: "The uploaded picture, a blob the draft holds." }),
    contentType: z.enum(["image/png", "image/jpeg", "image/webp"]),
    projection: z.enum(["plateCarree", "webMercator"]).meta({
      description: "How the picture lays the world out.",
    }),
    bounds: z
      .strictObject({
        west: z.number().min(-180).max(180),
        south: z.number().min(-90).max(90),
        east: z.number().min(-180).max(180),
        north: z.number().min(-90).max(90),
      })
      .meta({ description: "Where its edges are pinned, in degrees." }),
    opacity: z.number().min(0).max(1),
    visible: z.boolean(),
  })
  .meta({ id: "BasemapRoomOverlay" });
export type RoomOverlay = z.infer<typeof RoomOverlay>;

/** The most overlays a room holds. */
export const ROOM_MAX_OVERLAYS = 32;

/** One change a commit makes. */
export const RoomOp = z.discriminatedUnion("kind", [
  /**
   * Leaves given new pixels, or null to go back to the base's. The places above them are made from
   * them wherever they're needed: by an editor for its view, and by publishing.
   */
  z.strictObject({
    kind: z.literal("tiles"),
    raster: BasemapName,
    writes: z.array(z.strictObject({ place: RoomPlace, blob: Sha256.nullable() })).max(4096),
  }),
  /** A leaf made four, each a quarter of it at twice the detail. */
  z.strictObject({ kind: z.literal("split"), raster: BasemapName, place: RoomPlace }),
  /** A place made one leaf again, everything beneath it dropped. */
  z.strictObject({ kind: z.literal("merge"), raster: BasemapName, place: RoomPlace }),
  /** The whole document, as it is after. */
  z.strictObject({ kind: z.literal("document"), document: BasemapDraftDocument }),
  /**
   * A raster given a new base, such as an uploaded tile file, or none. Its tree becomes the base's
   * detail, and its changed tiles go back to the base's.
   */
  z.strictObject({ kind: z.literal("base"), raster: BasemapName, base: RoomRasterBase.nullable() }),
  /** Every overlay, as they are after. */
  z.strictObject({
    kind: z.literal("overlays"),
    overlays: z.array(RoomOverlay).max(ROOM_MAX_OVERLAYS),
  }),
  /** A feature set's whole features, a blob of `featuresToBlob`, or null to go back to its base's. */
  z.strictObject({ kind: z.literal("features"), featureSet: BasemapName, blob: Sha256.nullable() }),
]);
export type RoomOp = z.infer<typeof RoomOp>;

/**
 * What an undo or redo expects to find, so it never takes back someone else's later change: each
 * tile's blob, each place's shape, and the document, as the commit it reverses left them.
 */
export const RoomExpectation = z.strictObject({
  tiles: z
    .array(z.strictObject({ raster: BasemapName, place: RoomPlace, blob: Sha256.nullable() }))
    .max(4096)
    .default([]),
  leaves: z
    .array(z.strictObject({ raster: BasemapName, place: RoomPlace, leaf: z.boolean() }))
    .max(4096)
    .default([]),
  documentHash: z.string().max(64).optional(),
});
export type RoomExpectation = z.infer<typeof RoomExpectation>;

export const RoomCommit = z.strictObject({
  /** Chosen by the client, so it knows its own commit when it comes back. */
  id: z.string().min(8).max(64),
  label: z.string().max(200),
  ops: z.array(RoomOp).min(1).max(64),
  expect: RoomExpectation.optional(),
});
export type RoomCommit = z.infer<typeof RoomCommit>;

/** A changed tile: its raster, its place, and the blob holding its pixels. */
export interface RoomTile {
  raster: string;
  place: TilePlace;
  blob: string;
}

/** Everything a room holds beside its document, for storing, sending, or starting a copy. */
export interface RoomContents {
  base?: RoomBase | null;
  splits?: Record<string, TilePlace[]>;
  tiles?: RoomTile[];
  /** Each changed feature set's features blob, by name. */
  features?: Record<string, string>;
  overlays?: RoomOverlay[];
}

/** A draft as every copy of it holds it. */
export class RoomState {
  readonly trees = new Map<string, LeafTree>();
  /** Each changed tile's blob, by `raster:place`. */
  readonly tiles = new Map<string, string>();
  /** Each raster's base, by name. A raster without one starts empty. */
  readonly bases = new Map<string, RoomRasterBase>();
  /** Each feature set's base, by name. */
  readonly featureSetBases = new Map<string, RoomFeatureSetBase>();
  /** Each changed feature set's features blob, by name. A set without one is its base's. */
  readonly features = new Map<string, string>();
  overlays: RoomOverlay[];

  constructor(
    public document: BasemapDraftDocument,
    { base = null, splits = {}, tiles = [], features = {}, overlays = [] }: RoomContents = {},
  ) {
    for (const [name, raster] of Object.entries(base?.rasters ?? {})) this.bases.set(name, raster);
    for (const [name, set] of Object.entries(base?.featureSets ?? {})) {
      this.featureSetBases.set(name, set);
    }
    this.overlays = overlays;
    for (const [name, blob] of Object.entries(features)) this.features.set(name, blob);
    this.#plant(splits);
    for (const tile of tiles) this.tiles.set(tileKey(tile.raster, tile.place), tile.blob);
  }

  get faces(): number {
    return this.document.geometry.kind === "sphere" ? 6 : 1;
  }

  tree(raster: string): LeafTree | undefined {
    return this.trees.get(raster);
  }

  blobOf(raster: string, place: TilePlace): string | null {
    return this.tiles.get(tileKey(raster, place)) ?? null;
  }

  /** Every base, for storing or sending. */
  base(): RoomBase {
    return {
      rasters: Object.fromEntries(this.bases),
      featureSets: Object.fromEntries(this.featureSetBases),
    };
  }

  /** Each raster's splits, for storing or sending. */
  splits(): Record<string, TilePlace[]> {
    return Object.fromEntries([...this.trees].map(([name, tree]) => [name, tree.splits()]));
  }

  /** Every changed tile, for storing or sending. */
  changed(): RoomTile[] {
    return [...this.tiles].map(([key, blob]) => ({ ...untileKey(key), blob }));
  }

  /** Every changed feature set's features blob, for storing or sending. */
  changedFeatures(): Record<string, string> {
    return Object.fromEntries(this.features);
  }

  /** Every blob the room names: changed tiles and features, overlays, and uploaded bases. */
  blobs(): Set<string> {
    const named = new Set([...this.tiles.values(), ...this.features.values()]);
    for (const overlay of this.overlays) named.add(overlay.blob);
    for (const base of [...this.bases.values(), ...this.featureSetBases.values()]) {
      if (base.storage === "draft") named.add(base.tileFile);
    }
    return named;
  }

  /** Why a commit can't be applied as it is, or null when it can. */
  check(commit: RoomCommit): string | null {
    const expect = commit.expect;
    if (expect) {
      for (const tile of expect.tiles) {
        if (this.blobOf(tile.raster, tile.place) !== tile.blob) {
          return "Someone changed it since.";
        }
      }
      for (const leaf of expect.leaves) {
        if ((this.trees.get(leaf.raster)?.isLeaf(leaf.place) ?? false) !== leaf.leaf) {
          return "Someone changed its detail since.";
        }
      }
      if (
        expect.documentHash !== undefined &&
        expect.documentHash !== documentHash(this.document)
      ) {
        return "Someone changed the world's settings since.";
      }
    }
    // Tried on a copy of the trees, so a commit is all or nothing.
    let document = this.document;
    const trees = new Map([...this.trees].map(([name, tree]) => [name, tree.copy()]));
    for (const op of commit.ops) {
      if (op.kind === "document") {
        document = op.document;
        for (const [name, rules] of Object.entries(document.rasters)) {
          if (!trees.has(name)) trees.set(name, new LeafTree(rules.finestLevel, this.faces));
        }
        continue;
      }
      if (op.kind === "overlays") continue;
      if (op.kind === "features") {
        if (!document.featureSets[op.featureSet]) {
          return `There's no feature set called ${op.featureSet}.`;
        }
        continue;
      }
      const tree = trees.get(op.raster);
      const rules = document.rasters[op.raster];
      if (!tree || !rules) return `There's no raster called ${op.raster}.`;
      if (op.kind === "base") {
        if (!op.base) continue;
        if (op.base.rules.type !== rules.type) {
          return `${op.raster} is a ${rules.type} raster, and its base is ${op.base.rules.type}.`;
        }
        if (op.base.finestLevel !== rules.finestLevel) {
          return `${op.raster} starts at level ${rules.finestLevel}, and its base at ${op.base.finestLevel}.`;
        }
        try {
          trees.set(op.raster, decodeDetailMap(op.base.detailMap, op.base.finestLevel, this.faces));
        } catch (error) {
          return error instanceof Error ? error.message : String(error);
        }
        continue;
      }
      if (op.kind === "tiles") continue;
      try {
        if (op.kind === "split") tree.split(op.place);
        if (op.kind === "merge") tree.merge(op.place);
      } catch (error) {
        return error instanceof Error ? error.message : String(error);
      }
    }
    return null;
  }

  /** Applies a commit `check` passed. */
  apply(commit: RoomCommit): void {
    for (const op of commit.ops) {
      if (op.kind === "document") {
        this.document = op.document;
        this.#plant(this.splits());
      } else if (op.kind === "overlays") {
        this.overlays = op.overlays;
      } else if (op.kind === "features") {
        if (op.blob) this.features.set(op.featureSet, op.blob);
        else this.features.delete(op.featureSet);
      } else if (op.kind === "base") {
        this.#dropTiles(op.raster, () => true);
        if (op.base) {
          this.bases.set(op.raster, op.base);
          this.trees.set(
            op.raster,
            decodeDetailMap(op.base.detailMap, op.base.finestLevel, this.faces),
          );
        } else {
          this.bases.delete(op.raster);
          const rules = this.document.rasters[op.raster];
          if (rules) this.trees.set(op.raster, new LeafTree(rules.finestLevel, this.faces));
        }
      } else if (op.kind === "tiles") {
        for (const write of op.writes) {
          const key = tileKey(op.raster, write.place);
          if (write.blob) this.tiles.set(key, write.blob);
          else this.tiles.delete(key);
        }
      } else {
        const tree = this.trees.get(op.raster);
        if (!tree) continue;
        if (op.kind === "split") tree.split(op.place);
        else {
          // The tiles beneath a merged place go with it; the merge's own tiles op writes its pixels.
          this.#dropTiles(
            op.raster,
            place => place.level > op.place.level && under(place, op.place),
          );
          tree.merge(op.place);
        }
      }
    }
  }

  #dropTiles(raster: string, matches: (place: TilePlace) => boolean) {
    for (const key of [...this.tiles.keys()]) {
      const tile = untileKey(key);
      if (tile.raster === raster && matches(tile.place)) this.tiles.delete(key);
    }
  }

  /**
   * Each raster's tree, its base its document's finest level, keeping the splits it still can. A
   * raster or feature set the document no longer has loses its base, its tiles, and its features.
   */
  #plant(splits: Record<string, TilePlace[]>) {
    this.trees.clear();
    for (const [name, rules] of Object.entries(this.document.rasters)) {
      const base = rules.finestLevel;
      this.trees.set(
        name,
        new LeafTree(
          base,
          this.faces,
          (splits[name] ?? []).filter(place => place.level >= base),
        ),
      );
    }
    for (const name of [...this.bases.keys()]) {
      if (!this.document.rasters[name]) this.bases.delete(name);
    }
    for (const name of [...this.featureSetBases.keys()]) {
      if (!this.document.featureSets[name]) this.featureSetBases.delete(name);
    }
    for (const name of [...this.features.keys()]) {
      if (!this.document.featureSets[name]) this.features.delete(name);
    }
    for (const key of [...this.tiles.keys()]) {
      if (!this.trees.has(untileKey(key).raster)) this.tiles.delete(key);
    }
  }
}

/**
 * A published manifest as a room's base: each raster's bundle and rules, and each feature set's
 * file, all stored as `storage` says.
 */
export function baseFromManifest(manifest: BasemapManifest, storage: RoomFileStorage): RoomBase {
  const rasters: Record<string, RoomRasterBase> = {};
  for (const [name, { bundle: bundleName, ...rules }] of Object.entries(manifest.rasters)) {
    const bundle = manifest.bundles[bundleName];
    if (!bundle) continue;
    rasters[name] = {
      tileFile: bundle.tileFile,
      finestLevel: bundle.finestLevel,
      detailMap: bundle.detailMap,
      rules: rules as BasemapRasterRules,
      storage,
    };
  }
  const featureSets: Record<string, RoomFeatureSetBase> = {};
  for (const [name, { tileFile, detailMap, ...rules }] of Object.entries(manifest.featureSets)) {
    featureSets[name] = { tileFile, detailMap, rules, storage };
  }
  return { rasters, featureSets };
}

/** Each raster's splits as its base's detail map has them, for a room that begins from a base. */
export function splitsFromBase(base: RoomBase, faces: number): Record<string, TilePlace[]> {
  return Object.fromEntries(
    Object.entries(base.rasters).map(([name, raster]) => [
      name,
      decodeDetailMap(raster.detailMap, raster.finestLevel, faces).splits(),
    ]),
  );
}

export const tileKey = (raster: string, place: TilePlace) => `${raster}:${placeKey(place)}`;

function untileKey(key: string): { raster: string; place: TilePlace } {
  const at = key.lastIndexOf(":");
  const [face, level, x, y] = key
    .slice(at + 1)
    .split("/")
    .map(Number) as [number, number, number, number];
  return { raster: key.slice(0, at), place: { face, level, x, y } };
}

const under = (place: TilePlace, above: TilePlace) => {
  const shift = place.level - above.level;
  return (
    place.face === above.face &&
    Math.floor(place.x / 2 ** shift) === above.x &&
    Math.floor(place.y / 2 ** shift) === above.y
  );
};

/** A short fingerprint of a document, for an undo to say which one it expects. */
export function documentHash(document: BasemapDraftDocument): string {
  const text = JSON.stringify(document);
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index++) {
    hash = Math.imul(hash ^ text.charCodeAt(index), 0x01000193);
  }
  return (hash >>> 0).toString(16);
}
